import { Injectable } from '@angular/core';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import {
  NetworkFlexDatum,
  ProviderFlexLookupResult,
  ProviderFlexLookupResultStatus,
  ProviderFlexSearchItem,
  ProviderFlexSearchRequest
} from '../../../../../models/provider-flex';
import { DateUtility, isNullOrUndefined, isStringNullUndefinedOrEmptyWithTrim } from '../../../../utility';
import { Observable, of } from 'rxjs';
import { ExternalServiceLocationResponse } from '../../../../../models/externalServiceLocation';
import { ProviderFlexDataService } from '../provider-flex-data/provider-flex-data.service';
import { ExternalServiceLocationService } from '../external-service-location/external-service-location.service';
import { GeneralTablesService } from '../general-tables-data/general-tables.service';
import { commercialGapProgramTypeCode, effectiveDoctorTypeGapProvider } from './commercial-gap-provider.constants';
import { CommercialGapProviderByNetworkIdStorage } from './commercial-gap-provider-by-network-id-storage';

@Injectable({
  providedIn: 'root'
})
export class CommercialGapProviderService {

  constructor(
    private externalServiceLocationService: ExternalServiceLocationService,
    private providerFlexDataService: ProviderFlexDataService,
    private generalTablesService: GeneralTablesService,
    private commercialGapStorage: CommercialGapProviderByNetworkIdStorage,
  ) { }

  public shouldShowCommercialGapReport(networkId: string): Observable<boolean> {
    if (!networkId) { return of(false); }

    networkId = networkId.trim();

    const sessionStorageValue = this.commercialGapStorage.get(networkId);
    if (sessionStorageValue !== undefined) {
      return of(sessionStorageValue);
    }

    return this.loadIssuedAuthorizationProviderFlexItems(networkId).pipe(
      tap((providerFlexLookupResult: ProviderFlexLookupResult) => {
        this.providerFlexDataService.issuedAuthorizationProviderFlexLookupResult = providerFlexLookupResult;
      }),
      switchMap((providerFlexLookupResult: ProviderFlexLookupResult) =>
        this.isGapProvider(providerFlexLookupResult) ?
          this.isCommercialGapProvider(providerFlexLookupResult.networkId) :
          of(false)
      ),
      tap((shouldShowCommercialGapReport: boolean) =>
        this.commercialGapStorage.set(networkId, shouldShowCommercialGapReport))
    );
  }

  private isGapProvider(providerFlexLookupResult: ProviderFlexLookupResult): boolean {
    return providerFlexLookupResult.status === ProviderFlexLookupResultStatus.SUCCESS &&
      !isStringNullUndefinedOrEmptyWithTrim(providerFlexLookupResult.networkId) &&
      providerFlexLookupResult.effectiveDoctorType === effectiveDoctorTypeGapProvider;
  }

  private isCommercialGapProvider(networkId: string): Observable<boolean> {
    return this.generalTablesService
      .getProgramTypeCode(networkId).pipe(
        take(1),
        map((programTypeCode: string | undefined) =>
          programTypeCode === commercialGapProgramTypeCode
        ),
      );
  }

  private buildProviderFlexLookupResult = (status: ProviderFlexLookupResultStatus, networkId?: string, effectiveDoctorType?: string): ProviderFlexLookupResult =>
    status === ProviderFlexLookupResultStatus.SUCCESS ?
      !isStringNullUndefinedOrEmptyWithTrim(effectiveDoctorType) &&
      !isStringNullUndefinedOrEmptyWithTrim(networkId)
        ? { status, networkId: networkId.trim(), effectiveDoctorType: effectiveDoctorType.trim() }
        : { status: ProviderFlexLookupResultStatus.FAILED }
      : { status }

  private hasProviderFlexSearchRequestData = (externalServiceLocationResponse: ExternalServiceLocationResponse): boolean =>
    !isNullOrUndefined(externalServiceLocationResponse) &&
    !isStringNullUndefinedOrEmptyWithTrim(externalServiceLocationResponse.providerTaxId)

  private buildProviderFlexSearchRequest(externalServiceLocationResponse: ExternalServiceLocationResponse): ProviderFlexSearchRequest {
    const providerFlexSearchRequest: ProviderFlexSearchRequest = {
      providerTaxId: externalServiceLocationResponse.providerTaxId.trim(),
      activeFlag: true,
      asofdate: DateUtility.buildYyyyMmDdDateFromDate(new Date()),
    };

    if (!isNullOrUndefined(externalServiceLocationResponse.serviceLocationHashCode)) {
      providerFlexSearchRequest.serviceLocationHashCode = externalServiceLocationResponse.serviceLocationHashCode.trim();
    }

    if (!isNullOrUndefined(externalServiceLocationResponse.serviceLocationSequence)) {
      providerFlexSearchRequest.serviceLocationSequence = externalServiceLocationResponse.serviceLocationSequence.trim();
    }

    return providerFlexSearchRequest;
  }

  private hasEffectiveDoctorType = (effectiveDoctorType: string): boolean =>
    !isStringNullUndefinedOrEmptyWithTrim(effectiveDoctorType)

  private hasNetworkId = (networkId: string): boolean =>
    !isStringNullUndefinedOrEmptyWithTrim(networkId)

  private getFirstValidNetworkFlexDatumForNetworkId =
    (providerFlexSearchItems: ProviderFlexSearchItem[], networkId: string): NetworkFlexDatum | undefined => {
    const validNetworkFlexDatum = providerFlexSearchItems
    .flatMap((providerFlexSearchItem: ProviderFlexSearchItem) => providerFlexSearchItem?.networkFlexData ?? [])
    .find((networkFlexDatum: NetworkFlexDatum) =>
      this.hasEffectiveDoctorType(networkFlexDatum?.effectiveDoctorType) &&
      this.hasNetworkId(networkFlexDatum?.networkId) &&
      networkFlexDatum.networkId.trim() === networkId
    );

    return validNetworkFlexDatum ? (
      {
        ...validNetworkFlexDatum,
        networkId: validNetworkFlexDatum.networkId.trim(),
        effectiveDoctorType: validNetworkFlexDatum.effectiveDoctorType.trim(),
      })
      : undefined;
  }

  private loadIssuedAuthorizationProviderFlexItems(networkId: string): Observable<ProviderFlexLookupResult> {
    return this.externalServiceLocationService.getExternalServiceLocationOfficeInfo()
    .pipe(
      take(1),
      catchError((err) => {
        console.error('External service location call failed.', err);
        return of(this.buildProviderFlexLookupResult(ProviderFlexLookupResultStatus.FAILED));
      }),
      switchMap((externalServiceLocationResponse: ExternalServiceLocationResponse) => {
        if (isNullOrUndefined(externalServiceLocationResponse)) {
          console.error('External service location response is null or undefined.');
          return of(this.buildProviderFlexLookupResult(ProviderFlexLookupResultStatus.FAILED));
        }

        if (!this.hasProviderFlexSearchRequestData(externalServiceLocationResponse)) {
          console.error('External service location response does not have provider flex search request data.');
          return of(this.buildProviderFlexLookupResult(ProviderFlexLookupResultStatus.FAILED));
        }

        const providerFlexSearchRequest = this.buildProviderFlexSearchRequest(externalServiceLocationResponse);

        return this.providerFlexDataService
        .getProviderFlexSearchItems(providerFlexSearchRequest)
        .pipe(
          take(1),
          map((providerFlexSearchItems: ProviderFlexSearchItem[]) => {
            if (isNullOrUndefined(providerFlexSearchItems)) {
              console.error('Provider flex search items are null or undefined.');
              return this.buildProviderFlexLookupResult(ProviderFlexLookupResultStatus.FAILED);
            }

            if (providerFlexSearchItems.length === 0) {
              console.error('Provider flex search items are empty.');
              return this.buildProviderFlexLookupResult(ProviderFlexLookupResultStatus.FAILED);
            }

            const datum = this.getFirstValidNetworkFlexDatumForNetworkId(providerFlexSearchItems, networkId);

            return datum
              ? this.buildProviderFlexLookupResult(ProviderFlexLookupResultStatus.SUCCESS, datum.networkId, datum.effectiveDoctorType)
              : this.buildProviderFlexLookupResult(ProviderFlexLookupResultStatus.FAILED);
          }),
          catchError((err) => {
            console.error('Provider flex data service call failed.', err);
            return of(this.buildProviderFlexLookupResult(ProviderFlexLookupResultStatus.FAILED));
          })
        );
      }),
      catchError((err) => {
        console.error('Unhandled provider flex lookup error.', err);
        return of(this.buildProviderFlexLookupResult(ProviderFlexLookupResultStatus.FAILED));
      })
    );
  }
}
